/**
 * CheckoutModal — Modal de pagamento para upgrade de plano.
 * Coleta método de pagamento, CPF e dados do cartão (se aplicável).
 * Envia para /api/pagamento/upgrade e mostra resultado (PIX QR / Boleto / Sucesso).
 */
import React, { useState } from 'react';
import {
  X,
  CreditCard,
  QrCode,
  FileText,
  Loader2,
  CheckCircle2,
  AlertCircle,
  Copy,
  Check,
  ExternalLink,
} from 'lucide-react';

interface PlanoSelecionado {
  plano: string;
  periodo: 'mensal' | 'anual';
  usuarios: number;
  precoTotal: number;
  precoUnitario: number;
}

interface CheckoutModalProps {
  plano: PlanoSelecionado;
  userEmail: string;
  userName: string;
  onClose: () => void;
  onSuccess: () => void;
}

type PaymentMethod = 'PIX' | 'CREDIT_CARD' | 'BOLETO';
type Step = 'method' | 'details' | 'processing' | 'result';

const CheckoutModal: React.FC<CheckoutModalProps> = ({
  plano,
  userEmail,
  userName,
  onClose,
  onSuccess,
}) => {
  const [step, setStep] = useState<Step>('method');
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>('PIX');
  const [cpf, setCpf] = useState('');
  const [celular, setCelular] = useState('');

  // Credit card fields
  const [cardHolder, setCardHolder] = useState('');
  const [cardNumber, setCardNumber] = useState('');
  const [cardExpiry, setCardExpiry] = useState('');
  const [cardCvv, setCardCvv] = useState('');
  const [cep, setCep] = useState('');
  const [numero, setNumero] = useState('');

  // Result
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [paymentInfo, setPaymentInfo] = useState<any>(null);
  const [copied, setCopied] = useState(false);

  const billingPeriod = plano.periodo === 'mensal' ? 'monthly' : 'annual';
  const valorDisplay = plano.periodo === 'mensal'
    ? `R$ ${plano.precoTotal.toFixed(2).replace('.', ',')}/mês`
    : `R$ ${plano.precoTotal.toFixed(2).replace('.', ',')}/ano`;

  // ── Format helpers ──
  const formatCPF = (v: string) => {
    const digits = v.replace(/\D/g, '').slice(0, 11);
    return digits
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d{1,2})$/, '$1-$2');
  };

  const formatPhone = (v: string) => {
    const digits = v.replace(/\D/g, '').slice(0, 11);
    if (digits.length <= 2) return digits;
    if (digits.length <= 7) return `(${digits.slice(0, 2)}) ${digits.slice(2)}`;
    return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
  };

  const formatCardNumber = (v: string) => {
    const digits = v.replace(/\D/g, '').slice(0, 16);
    return digits.replace(/(\d{4})(?=\d)/g, '$1 ');
  };

  const formatExpiry = (v: string) => {
    const digits = v.replace(/\D/g, '').slice(0, 4);
    if (digits.length > 2) return `${digits.slice(0, 2)}/${digits.slice(2)}`;
    return digits;
  };

  const formatCEP = (v: string) => {
    const digits = v.replace(/\D/g, '').slice(0, 8);
    if (digits.length > 5) return `${digits.slice(0, 5)}-${digits.slice(5)}`;
    return digits;
  };

  // ── Submit ──
  const handleSubmit = async () => {
    setError('');
    setLoading(true);
    setStep('processing');

    try {
      const body: any = {
        email: userEmail,
        nome: userName,
        cpf: cpf.replace(/\D/g, ''),
        celular: celular.replace(/\D/g, ''),
        paymentMethod,
        billingPeriod,
        userCount: plano.usuarios,
      };

      if (paymentMethod === 'CREDIT_CARD') {
        const [expMonth, expYear] = cardExpiry.split('/');
        body.creditCard = {
          holderName: cardHolder,
          number: cardNumber.replace(/\s/g, ''),
          expiryMonth: expMonth,
          expiryYear: expYear?.length === 2 ? `20${expYear}` : expYear,
          ccv: cardCvv,
        };
        body.cep = cep.replace(/\D/g, '');
        body.numero = numero;
      }

      const resp = await fetch('/api/pagamento/upgrade', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });

      const result = await resp.json();

      if (result.success) {
        setSuccess(true);
        setPaymentInfo(result.paymentInfo || null);
        setStep('result');

        if (result.accountActivated) {
          // Credit card — instant activation
          setTimeout(() => onSuccess(), 2000);
        }
      } else {
        setError(result.error || 'Erro ao processar pagamento');
        setStep('details');
      }
    } catch (e: any) {
      setError(e.message || 'Erro de conexão');
      setStep('details');
    } finally {
      setLoading(false);
    }
  };

  const handleCopyPix = () => {
    if (paymentInfo?.pixQrCode) {
      navigator.clipboard.writeText(paymentInfo.pixQrCode);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const canSubmit = () => {
    if (!cpf || cpf.replace(/\D/g, '').length < 11) return false;
    if (paymentMethod === 'CREDIT_CARD') {
      return (
        cardHolder.length > 2 &&
        cardNumber.replace(/\s/g, '').length >= 13 &&
        cardExpiry.length >= 4 &&
        cardCvv.length >= 3 &&
        cep.replace(/\D/g, '').length === 8
      );
    }
    return true;
  };

  // ── Method selection step ──
  if (step === 'method') {
    return (
      <div className="fixed inset-0 z-[10001] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
          <div className="flex items-center justify-between p-6 border-b border-gray-100">
            <div>
              <h2 className="text-lg font-bold text-gray-900">Finalizar assinatura</h2>
              <p className="text-sm text-gray-500 mt-0.5">
                Plano Pro · {plano.usuarios} usuário{plano.usuarios > 1 ? 's' : ''} · {valorDisplay}
              </p>
            </div>
            <button onClick={onClose} className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100">
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="p-6 space-y-3">
            <p className="text-sm font-medium text-gray-700 mb-2">Como deseja pagar?</p>

            {([
              { id: 'PIX' as PaymentMethod, icon: QrCode, label: 'PIX', desc: 'Aprovação instantânea', badge: 'Recomendado' },
              { id: 'CREDIT_CARD' as PaymentMethod, icon: CreditCard, label: 'Cartão de Crédito', desc: 'Ativação imediata' },
              { id: 'BOLETO' as PaymentMethod, icon: FileText, label: 'Boleto Bancário', desc: '1-3 dias úteis para compensar' },
            ]).map((m) => (
              <button
                key={m.id}
                onClick={() => setPaymentMethod(m.id)}
                className={`w-full flex items-center gap-4 p-4 rounded-xl border-2 transition-all text-left ${
                  paymentMethod === m.id
                    ? 'border-indigo-500 bg-indigo-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                  paymentMethod === m.id ? 'bg-indigo-100 text-indigo-600' : 'bg-gray-100 text-gray-500'
                }`}>
                  <m.icon className="w-5 h-5" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-semibold text-gray-900">{m.label}</span>
                    {m.badge && (
                      <span className="text-[10px] font-bold text-indigo-600 bg-indigo-100 px-2 py-0.5 rounded-full">
                        {m.badge}
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-gray-500">{m.desc}</p>
                </div>
                <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                  paymentMethod === m.id ? 'border-indigo-500' : 'border-gray-300'
                }`}>
                  {paymentMethod === m.id && <div className="w-2.5 h-2.5 rounded-full bg-indigo-500" />}
                </div>
              </button>
            ))}
          </div>

          <div className="px-6 pb-6">
            <button
              onClick={() => setStep('details')}
              className="w-full py-3 rounded-xl text-sm font-bold text-white bg-indigo-600 hover:bg-indigo-700 transition-colors"
            >
              Continuar
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── Processing step ──
  if (step === 'processing') {
    return (
      <div className="fixed inset-0 z-[10001] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-8 text-center">
          <Loader2 className="w-12 h-12 text-indigo-600 animate-spin mx-auto mb-4" />
          <h3 className="text-lg font-bold text-gray-900 mb-1">Processando pagamento...</h3>
          <p className="text-sm text-gray-500">Aguarde enquanto confirmamos sua assinatura</p>
        </div>
      </div>
    );
  }

  // ── Result step ──
  if (step === 'result' && success) {
    const isPix = paymentMethod === 'PIX' && paymentInfo?.pixQrCode;
    const isBoleto = paymentMethod === 'BOLETO' && paymentInfo?.bankSlipUrl;
    const isCard = paymentMethod === 'CREDIT_CARD';

    return (
      <div className="fixed inset-0 z-[10001] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
          <div className="p-6 text-center">
            {isCard ? (
              <>
                <div className="w-16 h-16 mx-auto rounded-full bg-emerald-100 flex items-center justify-center mb-4">
                  <CheckCircle2 className="w-8 h-8 text-emerald-600" />
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-1">Pagamento confirmado!</h3>
                <p className="text-sm text-gray-500">Seu plano Pro está ativo. Redirecionando...</p>
              </>
            ) : isPix ? (
              <>
                <div className="w-16 h-16 mx-auto rounded-full bg-indigo-100 flex items-center justify-center mb-4">
                  <QrCode className="w-8 h-8 text-indigo-600" />
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">Pague com PIX</h3>
                <p className="text-sm text-gray-500 mb-4">
                  Escaneie o QR Code ou copie o código para pagar
                </p>

                {paymentInfo.pixImage && (
                  <div className="bg-gray-50 rounded-xl p-4 mb-4 inline-block">
                    <img
                      src={`data:image/png;base64,${paymentInfo.pixImage}`}
                      alt="QR Code PIX"
                      className="w-48 h-48 mx-auto"
                    />
                  </div>
                )}

                <button
                  onClick={handleCopyPix}
                  className="w-full flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-bold text-white bg-indigo-600 hover:bg-indigo-700 transition-colors mb-3"
                >
                  {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                  {copied ? 'Copiado!' : 'Copiar código PIX'}
                </button>

                <p className="text-xs text-gray-400">
                  Após o pagamento, seu plano será ativado automaticamente
                </p>
              </>
            ) : isBoleto ? (
              <>
                <div className="w-16 h-16 mx-auto rounded-full bg-amber-100 flex items-center justify-center mb-4">
                  <FileText className="w-8 h-8 text-amber-600" />
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">Boleto gerado!</h3>
                <p className="text-sm text-gray-500 mb-4">
                  O boleto pode levar até 3 dias úteis para compensar
                </p>

                <a
                  href={paymentInfo.bankSlipUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-bold text-white bg-indigo-600 hover:bg-indigo-700 transition-colors mb-3"
                >
                  <ExternalLink className="w-4 h-4" />
                  Abrir boleto
                </a>

                <p className="text-xs text-gray-400">
                  Após o pagamento, seu plano será ativado automaticamente
                </p>
              </>
            ) : (
              <>
                <div className="w-16 h-16 mx-auto rounded-full bg-emerald-100 flex items-center justify-center mb-4">
                  <CheckCircle2 className="w-8 h-8 text-emerald-600" />
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-1">Assinatura criada!</h3>
                <p className="text-sm text-gray-500">Seu plano será ativado após a confirmação do pagamento</p>
              </>
            )}
          </div>

          <div className="px-6 pb-6">
            <button
              onClick={onSuccess}
              className="w-full py-3 rounded-xl text-sm font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 transition-colors"
            >
              Fechar
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── Details step (CPF + card form) ──
  return (
    <div className="fixed inset-0 z-[10001] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-gray-100">
          <div>
            <h2 className="text-lg font-bold text-gray-900">Dados de pagamento</h2>
            <p className="text-sm text-gray-500 mt-0.5">
              {paymentMethod === 'PIX' ? 'PIX' : paymentMethod === 'CREDIT_CARD' ? 'Cartão de Crédito' : 'Boleto'} · {valorDisplay}
            </p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-4">
          {error && (
            <div className="flex items-start gap-2 bg-red-50 border border-red-200 rounded-xl px-4 py-3">
              <AlertCircle className="w-4 h-4 text-red-500 mt-0.5 shrink-0" />
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          {/* CPF */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">CPF</label>
            <input
              type="text"
              value={cpf}
              onChange={e => setCpf(formatCPF(e.target.value))}
              placeholder="000.000.000-00"
              className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-400 outline-none"
              maxLength={14}
            />
          </div>

          {/* Celular */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Celular <span className="text-gray-400">(opcional)</span></label>
            <input
              type="text"
              value={celular}
              onChange={e => setCelular(formatPhone(e.target.value))}
              placeholder="(00) 00000-0000"
              className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-400 outline-none"
              maxLength={15}
            />
          </div>

          {/* Credit card fields */}
          {paymentMethod === 'CREDIT_CARD' && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nome no cartão</label>
                <input
                  type="text"
                  value={cardHolder}
                  onChange={e => setCardHolder(e.target.value.toUpperCase())}
                  placeholder="NOME COMO ESTÁ NO CARTÃO"
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-400 outline-none"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Número do cartão</label>
                <input
                  type="text"
                  value={cardNumber}
                  onChange={e => setCardNumber(formatCardNumber(e.target.value))}
                  placeholder="0000 0000 0000 0000"
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-400 outline-none"
                  maxLength={19}
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Validade</label>
                  <input
                    type="text"
                    value={cardExpiry}
                    onChange={e => setCardExpiry(formatExpiry(e.target.value))}
                    placeholder="MM/AA"
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-400 outline-none"
                    maxLength={5}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">CVV</label>
                  <input
                    type="text"
                    value={cardCvv}
                    onChange={e => setCardCvv(e.target.value.replace(/\D/g, '').slice(0, 4))}
                    placeholder="123"
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-400 outline-none"
                    maxLength={4}
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">CEP</label>
                  <input
                    type="text"
                    value={cep}
                    onChange={e => setCep(formatCEP(e.target.value))}
                    placeholder="00000-000"
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-400 outline-none"
                    maxLength={9}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Número</label>
                  <input
                    type="text"
                    value={numero}
                    onChange={e => setNumero(e.target.value)}
                    placeholder="123"
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-400 outline-none"
                  />
                </div>
              </div>
            </>
          )}

          {/* Summary */}
          <div className="bg-gray-50 rounded-xl p-4 border border-gray-100">
            <div className="flex justify-between text-sm mb-1">
              <span className="text-gray-600">Plano Pro × {plano.usuarios} usuário{plano.usuarios > 1 ? 's' : ''}</span>
              <span className="font-semibold text-gray-900">{valorDisplay}</span>
            </div>
            <div className="flex justify-between text-xs text-gray-500">
              <span>{plano.periodo === 'mensal' ? 'Cobrança mensal' : 'Cobrança anual'}</span>
              <span>R$ {plano.precoUnitario.toFixed(2).replace('.', ',')}/usuário</span>
            </div>
          </div>
        </div>

        <div className="px-6 pb-6 flex gap-3">
          <button
            onClick={() => setStep('method')}
            className="px-4 py-3 rounded-xl text-sm font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 transition-colors"
          >
            Voltar
          </button>
          <button
            onClick={handleSubmit}
            disabled={!canSubmit() || loading}
            className="flex-1 py-3 rounded-xl text-sm font-bold text-white bg-indigo-600 hover:bg-indigo-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
          >
            {loading ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : paymentMethod === 'PIX' ? (
              'Gerar PIX'
            ) : paymentMethod === 'BOLETO' ? (
              'Gerar Boleto'
            ) : (
              'Pagar agora'
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CheckoutModal;
